// The Enthusiasm Company: E-man bubbles and add-to-cart confetti.

const config = window.TheEnthusiasm || {};
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* E-man bubbles: any bubble without its own line gets a random one. */

function fillBubbles(root = document) {
  const lines = (config.emanLines || []).filter(Boolean);
  if (!lines.length) return;

  const pool = [...lines];
  root.querySelectorAll('[data-te-eman-random]').forEach((bubble) => {
    if (!pool.length) pool.push(...lines);
    const index = Math.floor(Math.random() * pool.length);
    bubble.textContent = pool.splice(index, 1)[0];
  });
}

fillBubbles();
document.addEventListener('shopify:section:load', (event) => fillBubbles(event.target));

/* Add-to-cart: toast always, confetti only when motion is welcome. */

let toastTimer;

function showToast(message) {
  if (!message) return;

  let toast = document.querySelector('.te-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'te-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.append(toast);
  }

  toast.textContent = message;
  requestAnimationFrame(() => toast.classList.add('is-visible'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
}

/** Plays the confetti burst. Resolves when it's finished, or right away when skipped. */
function fireConfetti() {
  if (reduceMotion.matches || document.querySelector('.te-confetti')) return Promise.resolve();

  let done;
  const finished = new Promise((resolve) => (done = resolve));

  const canvas = document.createElement('canvas');
  canvas.className = 'te-confetti';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.append(canvas);

  const ctx = canvas.getContext('2d');
  const ratio = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * ratio;
  canvas.height = height * ratio;
  ctx.scale(ratio, ratio);

  const colors = config.confettiColors || ['#1B63C6', '#9ED8F6', '#0E0E10', '#FFD23F'];
  const pieces = Array.from({ length: 140 }, () => ({
    x: width / 2 + (Math.random() - 0.5) * width * 0.3,
    y: height * 0.35,
    vx: (Math.random() - 0.5) * 14,
    vy: Math.random() * -14 - 4,
    size: Math.random() * 7 + 5,
    spin: Math.random() * Math.PI,
    spinSpeed: (Math.random() - 0.5) * 0.3,
    color: colors[Math.floor(Math.random() * colors.length)],
  }));

  const start = performance.now();
  const duration = 1400;

  function frame(now) {
    const elapsed = now - start;
    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = Math.max(0, 1 - elapsed / duration);

    for (const p of pieces) {
      p.vy += 0.35;
      p.vx *= 0.99;
      p.x += p.vx;
      p.y += p.vy;
      p.spin += p.spinSpeed;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.spin);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }

    if (elapsed < duration) {
      requestAnimationFrame(frame);
    } else {
      canvas.remove();
      done();
    }
  }

  requestAnimationFrame(frame);

  // Browsers pause animation frames in background tabs. Never leave the canvas
  // (or the cart drawer) waiting on frames that may not come.
  setTimeout(() => {
    canvas.remove();
    done();
  }, duration + 250);

  return finished;
}

/* Open the cart drawer once the celebration is over, on every screen size. */

function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer.isOpen || typeof drawer.open !== 'function') return;

  // Quick add lives in a modal dialog. Let it close and restore focus first.
  const modal = document.querySelector('dialog[open]:modal:not(#cart-drawer dialog)');
  if (modal) {
    modal.addEventListener('close', () => requestAnimationFrame(() => drawer.isOpen || drawer.open()), { once: true });
    return;
  }

  drawer.open();
}

if (config.confetti || config.openCartAfterAdd) {
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;

    // Adds made inside the open drawer (cart add-ons) shouldn't reopen it.
    const drawer = document.getElementById('cart-drawer');
    const addedFromDrawer = drawer?.isOpen || event.target?.closest?.('#cart-drawer');

    const celebrate = async () => {
      if (config.confetti) {
        showToast(config.confettiMessage);
        await fireConfetti();
      }
      if (config.openCartAfterAdd && !addedFromDrawer) openCartDrawer();
    };

    if (event.promise) {
      event.promise.then(({ detail } = {}) => !detail?.didError && celebrate()).catch(() => {});
    } else {
      celebrate();
    }
  });
}

/* Cart add-ons: one-tap add through the theme's cart events so the drawer re-renders. */

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-te-upsell-add]');
  if (!button || button.disabled) return;

  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const { CartLinesUpdateEvent } = await import('@shopify/events');
  const root = window.Shopify?.routes?.root || '/';
  const id = Number(button.dataset.teUpsellAdd);
  const deferred = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: 'cart-drawer-section' }),
    }).then((r) => r.json());
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: Boolean(response.status), sections: response.sections, source: 'te-cart-upsell' },
    });
  } catch (error) {
    deferred.reject(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});
